import { useEffect, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  cn,
  Flex,
  Icon,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

import type {
  Commonv1Value,
  V1AutomationConfig,
  V1AutomationInputSpec,
  V1CodeManifest,
  V1CredentialInfo,
  V1Step,
} from '@/generated/hooks/grimoire/models';
import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import {
  getBrowserPanelState,
  registerBrowserPanelHost,
  setBrowserPanelOpen,
  subscribeToBrowserPanel,
} from '@/modules/outputs/browser-panel-registry';
import { PreviewLayover } from '@/modules/outputs/components/PreviewLayover';
import type { TResolvedFile } from '@/modules/outputs/types';
import type { IStepRunIO } from '@/modules/automation/hooks/useFocusedTestRunStatus';
import { useDraftRailCollapsed } from '@/shared/hooks/use-rail-preferences';
import { selectActiveTourId, useTourStore } from '@/shared/tours';

import {
  DraftActionToolbar,
  type IRailPrimaryAction,
} from './DraftActionToolbar';
import { RailBrowserSection } from './RailBrowserSection';
import { RailIntegrationsSection } from './RailIntegrationsSection';
import { RailLibrarySection } from './RailLibrarySection';
import { RailTriggersSection } from './RailTriggersSection';
import { RailTweaksSection } from './RailTweaksSection';
import { RailWidthToggle } from './RailWidthToggle';
import type { TStepRunStatus } from '@/modules/automation/utils/step-run-status';
import type { IResolvedExternalCall } from '@/modules/automation-run/utils/external-call-visibility';
import { DraftStepSummary } from '../DraftStepSummary';
import { SelectedStepPanel } from './SelectedStepPanel';
import { useRailIntegrations } from './use-rail-integrations';
import { useRailLibrary } from './use-rail-library';
import { useRailTriggers } from './use-rail-triggers';

/** Stable identity so the default doesn't re-run the schedule check each render. */
const EMPTY_DEFAULT_INPUT_KEYS: string[] = [];

export interface IDraftEditorRightRailProps {
  automationId: string;
  /** Automation display name, used in the mailbox address of a new email trigger */
  automationName?: string;
  manifest: V1CodeManifest;
  inputSpecs: V1AutomationInputSpec[];
  automationConfig?: V1AutomationConfig;
  onSaveConfigValue?: (name: string, value: Commonv1Value) => Promise<void>;
  credentialsByAlias: ReadonlyMap<string, V1CredentialInfo>;
  selectedStep?: V1Step;
  selectedStepStatus?: TStepRunStatus;
  selectedStepIO?: IStepRunIO;
  selectedStepRunningCalls?: IResolvedExternalCall[];
  /**
   * Inputs that already carry a saved default. A schedule runs unattended, so
   * the Triggers picker offers the schedule option only once every required
   * input has one.
   */
  defaultInputKeys?: string[];
  /**
   * Starts a run. Omit it where the rail is a record rather than a workspace —
   * the run page — and the Publish / Test / Run cluster goes with it.
   */
  onRun?: () => void;
  onTest?: () => void;
  onTestStep?: (stepId: string) => void;
  /** Hands an integration picked from the Integrations "+" to the editor. */
  onAddIntegration?: (integration: Bdkv2BookDescriptor) => void;
  canRun?: boolean;
  canTest?: boolean;
  isRunning?: boolean;
  isTesting?: boolean;
  /**
   * Where the rail sits. Beside a map (`map` on the draft page, `run` on the
   * run page) there is no document to show the selected step, so the rail
   * leads with its badge and title and opens with a Description block. `run`
   * also leaves out the needs-guidance pill, which the step's log on the run
   * map already shows.
   */
  variant?: 'draft' | 'map' | 'run';
  /** Placement overrides for the rail's root, e.g. the margin beside a map. */
  className?: string;
  /**
   * Takes the place of the Publish / Test / Run cluster with the phase's own
   * primary action — the blueprint's Approve & build. An unbuilt automation
   * has no code to run, test or publish, so offering all three disabled says
   * nothing; the build is the only thing to do from here.
   *
   * A description rather than a node, so the collapsed strip can render the
   * same action as an icon with its label in the tooltip.
   */
  primaryAction?: IRailPrimaryAction;
}

/** Draft-only right rail. It reorganizes existing run, trigger, integration,
 * file and config entry points without changing their underlying workflows. */
export function DraftEditorRightRail({
  automationId,
  automationName,
  manifest,
  inputSpecs,
  automationConfig,
  onSaveConfigValue,
  credentialsByAlias,
  selectedStep,
  selectedStepStatus,
  selectedStepIO,
  selectedStepRunningCalls,
  defaultInputKeys = EMPTY_DEFAULT_INPUT_KEYS,
  onRun,
  onTest,
  onTestStep,
  onAddIntegration,
  canRun = false,
  canTest = false,
  isRunning = false,
  isTesting = false,
  primaryAction,
  variant = 'draft',
  className,
}: IDraftEditorRightRailProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tBrowser } = useTranslation('browser-automation');
  const [isCollapsedPreference, setIsCollapsed] = useDraftRailCollapsed();
  // Tours anchor on the blocks (the draft-creation tour points at the
  // Triggers "+"), which a collapsed rail does not render.
  const activeTourId = useTourStore(selectActiveTourId);
  const isCollapsed = isCollapsedPreference && !activeTourId;

  // Hosts sessions even while collapsed, so none falls back into the chat.
  // eslint-disable-next-line no-restricted-syntax -- reason: registers in a module registry, with cleanup
  useEffect(() => registerBrowserPanelHost(), []);
  const { sessionIds: browserSessionIds, focusRequest } = useSyncExternalStore(
    subscribeToBrowserPanel,
    getBrowserPanelState,
  );
  // Only Opens made after mount expand the rail; the registry outlives the page.
  const [initialFocusRequest] = useState(focusRequest);
  // eslint-disable-next-line no-restricted-syntax -- reason: reacts to an explicit focus request raised outside React
  useEffect(() => {
    if (focusRequest !== initialFocusRequest) setIsCollapsed(false);
  }, [focusRequest, initialFocusRequest, setIsCollapsed]);
  const browserLabel = tBrowser('browserAutomation.liveView.title');

  // Not in the blocks: they unmount while a step is selected, so they'd refetch.
  const triggers = useRailTriggers(automationId, inputSpecs, defaultInputKeys);
  const integrations = useRailIntegrations(credentialsByAlias);
  const library = useRailLibrary(manifest);

  // Clicking an asset opens the same `PreviewLayover` the workspace library
  // opens from an asset row, fed the same `file-remote` resource name.
  const [previewFile, setPreviewFile] = useState<TResolvedFile | undefined>();

  const selectedStepLabel = tAutomation(
    'automation.draftRail.stepSelectedExpand',
    { number: selectedStep?.number },
  );

  const collapsedBrowserAction = browserSessionIds.length > 0 && (
    <Tooltip delayDuration={200} variant="primary">
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          onClick={() => {
            setIsCollapsed(false);
            setBrowserPanelOpen(true);
          }}
          className="shrink-0"
          aria-label={browserLabel}
          data-testid="draft-rail-collapsed-browser"
        >
          <Icon type="Globe" size="xs" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{browserLabel}</TooltipContent>
    </Tooltip>
  );

  return (
    <Flex
      direction="column"
      align={isCollapsed ? 'center' : 'stretch'}
      gap={4}
      className={cn(
        'ml-4 h-full shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out motion-reduce:transition-none',
        isCollapsed ? 'w-8' : 'w-[300px]',
        className,
      )}
      data-testid={
        isCollapsed
          ? 'draft-editor-right-rail-collapsed'
          : 'draft-editor-right-rail'
      }
    >
      {onRun ? (
        <DraftActionToolbar
          automationId={automationId}
          onRun={onRun}
          onTest={onTest}
          canRun={canRun}
          canTest={canTest}
          isRunning={isRunning}
          isTesting={isTesting}
          isCollapsed={isCollapsed}
          primaryAction={primaryAction}
          collapsedAction={collapsedBrowserAction}
        />
      ) : (
        // No run cluster beside a map, but a collapsed rail still needs its
        // way back to live browser sessions.
        isCollapsed && collapsedBrowserAction
      )}

      {/* Only the blocks scroll, so the width control keeps its corner. */}
      <Flex
        direction="column"
        align="stretch"
        gap={5}
        hidden={isCollapsed}
        className={cn(
          'no-scrollbar min-h-0 min-w-0 flex-1 overflow-y-auto',
          isCollapsed && 'hidden',
        )}
      >
        {/* Outside the step switch so the stream survives selecting a step; not
         * mounted while collapsed, where it would stream into a hidden box. */}
        {!isCollapsed && <RailBrowserSection />}
        {selectedStep ? (
          <SelectedStepPanel
            step={selectedStep}
            automationConfig={automationConfig}
            onSaveConfigValue={onSaveConfigValue}
            credentialsByAlias={credentialsByAlias}
            status={selectedStepStatus}
            io={selectedStepIO}
            runningCalls={selectedStepRunningCalls}
            onTestStep={onTestStep}
            header={
              variant !== 'draft' ? (
                <DraftStepSummary
                  step={selectedStep}
                  selected
                  showDescription={false}
                  nodeTestId={`draft-rail-step-node-${selectedStep.number}`}
                />
              ) : undefined
            }
            showDescription={variant !== 'draft'}
            hideException={variant === 'run'}
          />
        ) : (
          <>
            <RailTweaksSection
              automationConfig={automationConfig}
              onSaveConfigValue={onSaveConfigValue}
            />
            <RailTriggersSection
              automationId={automationId}
              automationName={automationName}
              inputSpecs={inputSpecs}
              triggers={triggers}
            />
            <RailIntegrationsSection
              automationId={automationId}
              integrations={integrations}
              onAddIntegration={onAddIntegration}
            />
            <RailLibrarySection
              library={library}
              onPreviewFile={setPreviewFile}
            />
          </>
        )}
        {/* Only where there are steps to select. On a blueprint the rail
          carries triggers and integrations for an automation that has not
          been built yet, so inviting a click on a step list that does not
          exist points at nothing. */}
        {!selectedStep && (manifest.steps?.length ?? 0) > 0 && (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap={2}
            className="min-h-36 flex-1 px-8 text-center"
            data-testid="draft-rail-step-empty-state"
          >
            <Icon
              type="MousePointerClick"
              size="md"
              className="text-muted-foreground"
            />
            <Text level="xSmall" color="muted">
              {tAutomation('automation.draftRail.selectStep')}
            </Text>
          </Flex>
        )}
      </Flex>

      {isCollapsed && <Flex flex="1" aria-hidden />}

      {isCollapsed && selectedStep?.number !== undefined && (
        <Tooltip delayDuration={200} variant="primary">
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              onClick={() => setIsCollapsed(false)}
              className="draft-selection-ring shrink-0 rounded-full border-2 bg-card text-xs font-medium tabular-nums text-foreground"
              aria-label={selectedStepLabel}
              data-testid="draft-rail-collapsed-selected-step"
            >
              {selectedStep.number}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{selectedStepLabel}</TooltipContent>
        </Tooltip>
      )}

      <Flex justify="end" align="center" className="shrink-0">
        <RailWidthToggle
          isCollapsed={isCollapsed}
          onToggle={() => setIsCollapsed(!isCollapsed)}
        />
      </Flex>

      {/* Mounted outside the section switch so a preview opened from the
       * Library survives selecting a step. */}
      <PreviewLayover
        isOpen={previewFile !== undefined}
        outputName=""
        outputValue={previewFile ?? null}
        onClose={() => setPreviewFile(undefined)}
        showCloseButton
      />
    </Flex>
  );
}
